import { onlineVisitFactorUrl } from '@/common/utils/onlineVisitFactorUrl';
import { useSearch } from '@/common/apis/services/search/search';
import Button from '@/common/components/atom/button';
import Loading from '@/common/components/atom/loading';
import Modal from '@/common/components/atom/modal';
import Text from '@/common/components/atom/text/text';
import useCustomize from '@/common/hooks/useCustomize';
import useModal from '@/common/hooks/useModal';
import buildSubstituteDoctorSearch, { SUBSTITUTE_DOCTOR_SEARCH_REF, buildSubstituteDoctorSearchText } from '@/common/utils/buildSubstituteDoctorSearch';
import { buildInPersonLine, resolveInPersonCity } from '@/common/utils/substituteInPersonLine';
import { splunkInstance } from '@/common/services/splunk';
import { doctorSlugFromSearchHit, useRaviCardRate } from '@/modules/profile/hooks/useDoctorRateDetails';
import SearchCard from '@/modules/search/components/card/card';
import { useSearchRouting } from '@/modules/search/hooks/useSearchRouting';
import random from 'lodash/random';
import { useEffect, useMemo, useRef } from 'react';

interface SubstituteDoctorButtonProps {
  displayName: string;
  expertises: any;
  doctorCity?: string;
  /** City for the flag-gated in-person line only: the doctor's own city, else their first clinic's.
   *  Kept separate from `doctorCity` so the legacy online-visit search keeps its nationwide
   *  fallback when a doctor has no city (review 2026-09-20). */
  inPersonCity?: string;
  buttonId?: string;
  buttonText?: string;
  autoOpenDelay?: number;
}

const SubstituteDoctorButton = ({
  displayName,
  expertises,
  doctorCity,
  inPersonCity,
  buttonId = 'substitute-doctor-button',
  buttonText = 'دریافت نوبت از پزشک جایگزین',
  autoOpenDelay = 6000,
}: SubstituteDoctorButtonProps) => {
  const { handleOpen, modalProps } = useModal();
  const customize = useCustomize(state => state.customize);
  const searchData = useSearch(buildSubstituteDoctorSearch(expertises?.expertises, doctorCity), {
    enabled: !customize.partnerKey,
  });
  // Online visit stays the main action; in-person is offered as one quieter line (owner 2026-09-18,
  // ghane WA §16.9.1). Always on — the former GrowthBook `profile:substitute-online-first` flag was
  // forced true for everyone and has been removed; the Splunk `variant` stays 'online-first'.
  const inPersonText = buildSubstituteDoctorSearchText(expertises?.expertises);
  // The specialty's city page (/s/<city>/<group>/) pins the city in the path. A free-text search on
  // /s/<city>/?text= lets the visitor's saved city override it (seen 2026-09-19: Isfahan link showed
  // Tehran with "no results"). Text search stays only as the fallback when the group has no slug.
  const groupSlug: string | undefined = expertises?.group_expertises?.[0]?.en_slug;
  // Only the in-person line narrows to a clinic city; `doctorCity` stays as the profile sent it so
  // `buildSubstituteDoctorSearch` keeps falling back to 'ir' (nationwide) for a doctor with no city.
  const inPersonCityResolved = resolveInPersonCity(inPersonCity, doctorCity);
  const inPersonRoute = decodeURIComponent(inPersonCityResolved || '') + (groupSlug ? `/${groupSlug}` : '');
  const inPersonSearch = useSearch(
    {
      route: inPersonRoute,
      query: groupSlug ? { ref: SUBSTITUTE_DOCTOR_SEARCH_REF } : { ref: SUBSTITUTE_DOCTOR_SEARCH_REF, text: inPersonText },
    },
    { enabled: !customize.partnerKey && !!inPersonCityResolved && (!!groupSlug || !!inPersonText) },
  );
  const inPersonLine = useMemo(
    () => buildInPersonLine(inPersonSearch.data?.search?.result, displayName),
    [inPersonSearch.data, displayName],
  );
  const groupName = expertises?.group_expertises?.[0]?.name;
  const { changeRoute } = useSearchRouting();

  // `trigger` separates the popup appearing by itself from the visitor pressing the button. Both
  // used to emit the same event, so the A/B's modal-view denominator mixed two different
  // behaviours and no analyst could tell them apart (review 2026-09-20).
  const handleOpenSubstituteDoctorModal = (trigger: 'auto' | 'click' = 'click') => {
    splunkInstance('search').sendEvent({
      group: 'profile_visit_online_doctor_recommendation',
      type: 'profile_visit_online_doctor_recommendation-open-modal',
      event: {
        slug: substituteDoctor?.url?.replace('/dr/', ''),
        // Which popup the visitor saw, so the 20% test can be measured per group (DR-41).
        variant: 'online-first',
        trigger,
      },
    });
    handleOpen();
  };

  const handleClickDoctorCard = ({ doctor, element }: { doctor: any; element: any }) => {
    const url: string = doctor.url ?? '';
    splunkInstance('search').sendEvent({
      group: 'profile_visit_online_doctor_recommendation',
      type: 'profile_visit_online_doctor_recommendation-click-doctor-card',
      event: {
        slug: url.replace('/dr/', ''),
        variant: 'online-first',
      },
    });
    return location.assign(onlineVisitFactorUrl(doctor));
  };

  const handleClickInPersonLine = () => {
    splunkInstance('search').sendEvent({
      group: 'profile_visit_online_doctor_recommendation',
      type: 'profile_visit_online_doctor_recommendation-click-inperson-line',
      event: {
        slug: substituteDoctor?.url?.replace('/dr/', ''),
        city: inPersonCityResolved,
        count: inPersonLine?.count,
      },
    });
    location.assign(
      groupSlug
        ? `/s/${inPersonCityResolved}/${groupSlug}/`
        : `/s/${inPersonCityResolved}/?text=${encodeURIComponent(inPersonText)}`,
    );
  };

  const handleClickMoreDoctors = () => {
    splunkInstance('search').sendEvent({
      group: 'profile_visit_online_doctor_recommendation',
      type: 'profile_visit_online_doctor_recommendation-click-more-doctors',
      event: {
        slug: substituteDoctor?.url?.replace('/dr/', ''),
        // Which popup the visitor saw, so the 20% test can be measured per group (DR-41).
        variant: 'online-first',
      },
    });
    changeRoute({
      query: {
        turn_type: 'consult',
        text: expertises?.expertises?.map((expertise: any) => expertise.expertise_name)[0],
      },
      params: {
        city: 'ir',
      },
      previousQueries: false,
    });
  };

  const substituteDoctor = useMemo(() => searchData.data?.search?.result?.[random(0, 2)] ?? {}, [searchData.data]);


  // The auto-open fires ~6s after the substitute arrives, and it reports which popup the visitor
  // saw. Two things it must not do (both seen 2026-09-20):
  //   * fire with a stale handler (it once captured a GrowthBook value that resolved later, so the
  //     event and the rendered popup disagreed);
  //   * survive the visitor leaving — an uncleared timer counts a modal view nobody ever saw and
  //     inflates the denominator of whichever arm they were in.
  // A ref keeps the handler current, so the event is written with the variant at FIRE time — the
  // same state the modal renders with — and the cleanup drops the timer on unmount.
  const openModalRef = useRef(handleOpenSubstituteDoctorModal);
  useEffect(() => {
    openModalRef.current = handleOpenSubstituteDoctorModal;
  });

  useEffect(() => {
    if (!substituteDoctor?.url || autoOpenDelay <= 0) return;
    const timer = setTimeout(() => openModalRef.current('auto'), autoOpenDelay);
    return () => clearTimeout(timer);
  }, [substituteDoctor?.url, autoOpenDelay]);

  // Don't render if partner key is present or no substitute doctor found
  if (customize.partnerKey || !substituteDoctor?.url) {
    return null;
  }

  return (
    <>
      <Button id={buttonId} block onClick={() => handleOpenSubstituteDoctorModal('click')}>
        <Text>{buttonText}</Text>
      </Button>

      <Modal name="substitute-doctor-button" bodyClassName="p-3" title="" {...modalProps}>
        {(searchData.isLoading || !substituteDoctor?.url) && (
          <div className="flex justify-center w-full">
            <Loading className="w-8 h-8 my-8 " />
          </div>
        )}
        {searchData.isSuccess && substituteDoctor?.url && (
          <div className="flex flex-col mb-2 space-y-2">
            <div className="flex bg-slate-50 border-slate-200 text-slate-900 items-center p-3 space-s-2 rounded-md border">
              <Text className="text-sm font-medium">{displayName} نوبت اینترنتی ندارد.</Text>
            </div>
            <SubstituteDoctorSuggestion doctor={substituteDoctor} groupName={groupName} onSelect={element => handleClickDoctorCard({ doctor: substituteDoctor, element })} />
          </div>
        )}
        {inPersonLine && (
          <Button block size="sm" className="text-xs" variant="text" onClick={handleClickInPersonLine}>
            ترجیح می‌دهید حضوری بروید؟ {inPersonLine.count} پزشک {groupName ?? ''}
            {inPersonLine.cityName ? ` در ${inPersonLine.cityName}` : ''}، اولین نوبت {inPersonLine.earliest}
          </Button>
        )}
        <Button block size="sm" className="text-xs opacity-70" variant="text" onClick={handleClickMoreDoctors}>
          مشاهده سایر پزشکان آنلاین {expertises?.group_expertises?.[0]?.name} مشابه
        </Button>
      </Modal>
    </>
  );
};


export const SubstituteDoctorSuggestion = ({ doctor, groupName, onSelect, hideBanner = false }: { doctor: any; groupName?: string; onSelect: (element: 'card' | 'button') => void; hideBanner?: boolean }) => {
  const raviRate = useRaviCardRate(doctorSlugFromSearchHit(doctor));
  return <div className="flex flex-col mb-2 space-y-2">
            {!hideBanner && <div className="p-3 bg-green-600 border-green-700 text-white rounded-md border flex flex-col space-y-1">
              <Text className="text-sm font-medium">همین امروز با {groupName ? `متخصص ${groupName}` : 'پزشک'} آنلاین مشورت کنید</Text>
              <Text className="text-xs">بدون رفت‌وآمد و صف · پیگیری در همان گفت‌وگو</Text>
            </div>}
            <div onClick={event => { if (!(event.target as HTMLElement).closest('button')) onSelect('card'); }}>
              <SearchCard
                avatarSize="lg"
                baseInfo={{
                  displayName: doctor.title,
                  expertise: doctor.display_expertise,
                  isVerify: true,
                  avatar: doctor.image,
                  rate: raviRate,
                  isOnline: true,
                }}
                type="doctor"
                className="shadow-none !py-0 lg:!py-0 cursor-pointer"
                actions={[
                  {
                    text: `ویزیت آنلاین با دکتر ${doctor.title}`,
                    outline: false,
                    description: '',
                    action() {
                      onSelect('button');
                    },
                  },
                ]}
              />
            </div>
  </div>;
};
export default SubstituteDoctorButton;
